import React, { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Bot, Plus, Search, Pencil, Archive, ArchiveRestore, Trash2, Download, Check, X as XIcon,
} from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import ChatView from "@/components/assistant/ChatView";
import { moduleForPath } from "@/components/assistant/moduleMap";
import { api } from "@/lib/api";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function AssistantCenter() {
  const location = useLocation();
  const [bootstrap, setBootstrap] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [q, setQ] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [renaming, setRenaming] = useState(null); // {id, title}
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    api.get("/assistant/bootstrap").then((r) => setBootstrap(r.data)).catch(() => {});
  }, []);

  const load = useCallback(() => {
    api.get(`/assistant/conversations?q=${encodeURIComponent(q)}&archived=${showArchived ? "1" : "0"}`)
      .then((r) => setConversations(r.data?.conversations || []))
      .catch(() => setConversations([]));
  }, [q, showArchived]);
  useEffect(() => { load(); }, [load]);

  const rename = async () => {
    try {
      await api.patch(`/assistant/conversations/${renaming.id}`, { title: renaming.title });
      setRenaming(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't rename"); }
  };

  const toggleArchive = async (c) => {
    try {
      await api.patch(`/assistant/conversations/${c.conversation_id}`, { archived: !c.archived });
      if (activeId === c.conversation_id) setActiveId(null);
      load();
    } catch { toast.error("Couldn't update"); }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/assistant/conversations/${deleting}`);
      if (activeId === deleting) setActiveId(null);
      setDeleting(null);
      load();
    } catch { toast.error("Couldn't delete"); }
  };

  const exportConv = (c) => {
    window.open(`${BACKEND}/api/assistant/conversations/${c.conversation_id}/export`, "_blank");
  };

  if (bootstrap && (!bootstrap.enabled || !bootstrap.plan_allowed)) {
    return (
      <div className="p-10 text-center" data-testid="assistant-unavailable">
        <Bot className="h-10 w-10 mx-auto text-ink-muted mb-3" />
        <p className="text-sm text-ink-secondary">
          {bootstrap.enabled ? "The assistant isn't included in your current plan." : "The BroadcastFlow Assistant is currently unavailable."}
        </p>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto animate-fade-up h-[calc(100vh-56px)] flex flex-col" data-testid="assistant-center-page">
      <PageHeader
        eyebrow="// BROADCASTFLOW ASSISTANT"
        title="AI Assistant Center"
        description="Longer conversations, content production and your full chat history with Flow."
      />
      <div className="flex-1 min-h-0 grid grid-cols-1 md:grid-cols-[280px_1fr] gap-4 mt-2">
        <aside className="rounded-xl border border-line bg-bg-surface flex flex-col min-h-0 max-md:max-h-56">
          <div className="p-3 space-y-2 border-b border-line">
            <button onClick={() => setActiveId(null)}
                    className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-brand-amber px-3 py-2 text-black text-sm font-semibold hover:bg-brand-amber/90 transition"
                    data-testid="center-new-conversation">
              <Plus className="h-4 w-4" /> New conversation
            </button>
            <div className="relative">
              <Search className="h-3.5 w-3.5 absolute left-2.5 top-2.5 text-ink-muted" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search conversations…"
                     className="w-full rounded-md border border-line bg-bg-elevated pl-8 pr-3 py-1.5 text-xs text-ink-primary placeholder:text-ink-muted focus:outline-none focus:border-brand-amber/60"
                     data-testid="center-search-input" />
            </div>
            <button onClick={() => setShowArchived((v) => !v)} className="text-[11px] text-ink-muted hover:text-ink-primary transition" data-testid="center-archived-toggle">
              {showArchived ? "← Back to active" : "View archived"}
            </button>
          </div>
          <div className="flex-1 overflow-y-auto min-h-0" data-testid="center-conversation-list">
            {conversations.length === 0 && (
              <p className="text-xs text-ink-muted p-4 text-center">{showArchived ? "No archived conversations." : "No conversations yet — start one!"}</p>
            )}
            {conversations.map((c) => (
              <div key={c.conversation_id}
                   className={`group px-3 py-2.5 border-b border-line/40 cursor-pointer transition ${activeId === c.conversation_id ? "bg-brand-amber/10 border-l-2 border-l-brand-amber" : "hover:bg-bg-elevated/50"}`}
                   onClick={() => setActiveId(c.conversation_id)}
                   data-testid={`center-conv-${c.conversation_id}`}>
                {renaming?.id === c.conversation_id ? (
                  <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                    <input value={renaming.title} onChange={(e) => setRenaming({ ...renaming, title: e.target.value })}
                           onKeyDown={(e) => e.key === "Enter" && rename()}
                           className="flex-1 rounded border border-line bg-bg-elevated px-2 py-1 text-xs" autoFocus data-testid="center-rename-input" />
                    <button onClick={rename} className="p-1 text-emerald-400" data-testid="center-rename-save"><Check className="h-3.5 w-3.5" /></button>
                    <button onClick={() => setRenaming(null)} className="p-1 text-ink-muted"><XIcon className="h-3.5 w-3.5" /></button>
                  </div>
                ) : (
                  <>
                    <div className="text-xs font-semibold text-ink-primary truncate">{c.title}</div>
                    <div className="text-[11px] text-ink-muted truncate mt-0.5">{c.last_snippet || "…"}</div>
                    <div className="flex items-center gap-1 mt-1 opacity-0 group-hover:opacity-100 transition" onClick={(e) => e.stopPropagation()}>
                      <button onClick={() => setRenaming({ id: c.conversation_id, title: c.title })} className="p-1 text-ink-muted hover:text-ink-primary" title="Rename" data-testid="center-rename-btn"><Pencil className="h-3 w-3" /></button>
                      <button onClick={() => toggleArchive(c)} className="p-1 text-ink-muted hover:text-ink-primary" title={c.archived ? "Restore" : "Archive"} data-testid="center-archive-btn">
                        {c.archived ? <ArchiveRestore className="h-3 w-3" /> : <Archive className="h-3 w-3" />}
                      </button>
                      <button onClick={() => exportConv(c)} className="p-1 text-ink-muted hover:text-ink-primary" title="Export" data-testid="center-export-btn"><Download className="h-3 w-3" /></button>
                      <button onClick={() => setDeleting(c.conversation_id)} className="p-1 text-ink-muted hover:text-red-400" title="Delete" data-testid="center-delete-btn"><Trash2 className="h-3 w-3" /></button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </aside>

        <section className="rounded-xl border border-line bg-bg-surface flex flex-col min-h-0">
          <ChatView
            conversationId={activeId}
            onConversationChange={(id) => { setActiveId(id); load(); }}
            module={moduleForPath(location.pathname)}
            bootstrap={bootstrap}
          />
        </section>
      </div>

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent className="bg-bg-surface border-line" data-testid="center-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete conversation?</AlertDialogTitle>
            <AlertDialogDescription>This permanently removes the conversation and its messages.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-500 hover:bg-red-600 text-white" data-testid="center-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
